import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Inject, Input, Output, computed, input } from '@angular/core';

import { BetSlipType } from '@bpos';
import { BetType } from '@bpos/common';
import { Sitecore } from '@frontend/sports/common/client-config-data-access';
import { StringInterpolationPipe } from '@frontend/sports/common/core/utils/string-interpolation';
import { ThemeOptions } from '@frontend/sports/common/ui/shared';
import { SportsThemedIconButtonDirective } from '@frontend/sports/common/ui/sports-themed-button';
import { OddsFormatterService } from '@frontend/sports/odds/feature';
import { RacingOptionMarketTypes } from '@frontend/sports/racing/feature/models';
import { OddsDisplayFormat } from '@frontend/sports/types/models/user-settings';
import { SyncUserSettingsService } from '@frontend/sports/user/feature';
import { DsButtonIcon } from '@frontend/ui/button-icon';
import { IconCustomComponent } from '@frontend/vanilla/shared/icons';
import { Store } from '@ngrx/store';
import { NgxFloatUiPlacements } from 'ngx-float-ui';
import { MarketSubtypeLogoComponent } from 'packages/sports/web/app/src/marketsubtype-logo/marketsubtype-logo.component';
import { PriceboostCountSignpostComponent } from 'packages/sports/web/app/src/shared-priceboost/Components/priceboost-count-signpost.component';
import { Observable, map } from 'rxjs';

import {
    BETSLIP_MESSAGES_PROVIDER_INJECTION_TOKEN,
    IBetslipMessagesProviderService,
} from '../../../../base/services/betslip-messages-provider.service';
import { BetslipBetBuilderPick } from '../../../../core/picks/betslip-bet-builder-pick';
import { BaseBetslipGroupPick } from '../../../../core/picks/betslip-group-pick';
import { QuickBetPickType } from '../../../../core/picks/pick-models';
import { isBetBuilderPick } from '../../../../core/utils';
import { ISucceededSlipResult } from '../../../betplacement/models';
import PickOddsModule from '../../../picks/pick-odds.module';
import { BetslipMarketSubTypeLogoService } from '../../../picks/services/betslip-market-sub-type-logo.service';
import BetslipDigitalResultModule from '../../../result/betslip-digital-result.module';
import { provideSummaryMessages } from '../../../result/utils/result-summary.utils';
import { betPlacedResultSelector } from '../../quick-bet.selectors';
import { QuickBetBetslipResultSummaryViewModel, QuickBetResultSlipProcessorService } from '../../services/quick-bet-result-slip-processor.service';

@Component({
    selector: 'bs-quick-bet-confirmation-compact',
    templateUrl: './quick-bet-confirmation-compact.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrls: ['./quick-bet-confirmation-compact.component.scss'],
    imports: [
        DsButtonIcon,
        IconCustomComponent,
        CommonModule,
        BetslipDigitalResultModule,
        MarketSubtypeLogoComponent,
        SportsThemedIconButtonDirective,
        PickOddsModule,
        PriceboostCountSignpostComponent,
    ],
})
export class QuickBetConfirmationCompactComponent {
    @Input() oddsDisplayFormat: OddsDisplayFormat;
    @Output() closeConfirmation = new EventEmitter<boolean>();

    resultState = input.required<ISucceededSlipResult>();

    summaryViewModel$: Observable<QuickBetBetslipResultSummaryViewModel>;

    pickInfo = computed(() => {
        const optionNames = this.getOptionNames();
        const quickBetPickType = this.quickBetPickType();

        if (quickBetPickType === QuickBetPickType.SinglePick) {
            return {
                header: optionNames,
                option: this.getOption(),
            };
        }

        return {
            header: quickBetPickType === QuickBetPickType.ComboPicks ? this.getComboBetTitle() : this.sitecore.globalMessages.BetBuilderBadgeText,
            option: optionNames,
        };
    });

    private getOption(): string | undefined {
        const pick = this.resultState().picks[0];
        if (
            pick.clientPick?.market.marketType?.toString() === RacingOptionMarketTypes.WinEw ||
            [BetType.Forecast, BetType.Tricast, BetType.CombinationForecast, BetType.CombinationTricast].includes(pick.optionBetDetails?.betType!)
        ) {
            return pick.optionBetDetails?.betTypeName;
        }

        return pick.marketName;
    }

    marketLogoInfo = computed(() => {
        const showLogo =
            this.quickBetPickType() === QuickBetPickType.SinglePick &&
            !!this.betslipMarketSubTypeLogoService.getMarketSubTypeLogoTitle(this.resultState().picks[0].clientPick);

        const pick = showLogo ? this.resultState().picks[0].clientPick : null;

        return { showLogo, pick };
    });

    isSinglePickPriceBoost = computed(() => {
        const quickBetPickType = this.quickBetPickType();
        if (quickBetPickType !== QuickBetPickType.SinglePick) {
            return false;
        }

        return !!this.resultState().picks[0].clientPick?.boostedPriceDetails?.isPriceBoosted;
    });

    readonly closeButtonThemeInverse: ThemeOptions<boolean> = {
        'betboo': true,
        'sportingbet-2': true,
        'gamebookers': true,
    };

    constructor(
        private store: Store,
        private quickBetResultSlipProcessorService: QuickBetResultSlipProcessorService,
        public sitecore: Sitecore,
        private interpolationPipe: StringInterpolationPipe,
        @Inject(BETSLIP_MESSAGES_PROVIDER_INJECTION_TOKEN) private messagesProvider: IBetslipMessagesProviderService,
        private betslipMarketSubTypeLogoService: BetslipMarketSubTypeLogoService,
        private userSettings: SyncUserSettingsService,
        private oddsFormatter: OddsFormatterService,
    ) {
        const messages = messagesProvider.getMessages();

        this.summaryViewModel$ = this.store.select(betPlacedResultSelector).pipe(
            map((qbSummary) => this.quickBetResultSlipProcessorService.buildSummaryViewModel(qbSummary, this.userSettings.configSettings)),
            map((buildSummaryViewModel) => {
                if (!buildSummaryViewModel) {
                    return null;
                }

                const betslipSummary = provideSummaryMessages(buildSummaryViewModel.summary, false, {
                    BoostedWinnings: messages.common.BoostedWinnings,
                    numberOfBets: messages.result.numberOfBets,
                    possibleWinnings: messages.result.possibleWinnings,
                    possibleWinningsGross: messages.result.possibleWinningsGross,
                    possibleWinningsNet: messages.result.possibleWinningsNet,
                    stake: messages.result.stake,
                    totalStake: messages.result.totalStake,
                    totalOdds: messages.result.totalOdds,
                    freebetText: messages.crmTokens.oddsBoost.betslipSummary.PayoutTypeFreebet,
                });

                if (!betslipSummary) {
                    return null;
                }

                return {
                    summary: betslipSummary,
                    startingPrice: {
                        hasStartingPrice: buildSummaryViewModel.startingPrice.hasStartingPrice,
                        startingPriceTitle: messages.common.StartingPrice,
                    },
                };
            }),
        );
    }

    closeResultScreen(): void {
        this.closeConfirmation.emit(true);
    }

    private getOptionNames() {
        const optionNames: string[] = [];
        for (const pick of this.resultState().picks) {
            if (pick.clientPick && BetslipBetBuilderPick.isPick(pick.clientPick)) {
                optionNames.push(...pick.clientPick.getLegs());
            } else if (pick.clientPick && BaseBetslipGroupPick.isPick(pick.clientPick)) {
                optionNames.push(...pick.clientPick.getLegs().map((p) => p.optionName.name));
            } else {
                optionNames.push(...pick.optionNames);
            }
        }

        return optionNames.join(', ');
    }

    private quickBetPickType = computed(() => {
        const picks = this.resultState().picks;
        if (picks.length === 1 && picks[0].clientPick && isBetBuilderPick(picks[0].clientPick)) {
            return QuickBetPickType.BetBuilderPicks;
        }

        return this.resultState().betType === BetSlipType.Single ? QuickBetPickType.SinglePick : QuickBetPickType.ComboPicks;
    });

    private getComboBetTitle(): string {
        return `${this.interpolationPipe.transform(this.sitecore.betslip.linear.PicksCount, {
            count: this.resultState().betCount,
        })} ${this.messagesProvider.getMessages().betTypes.Combo}`;
    }

    //this is only used in case of single pick which guranteed by isSinglePickPriceBoost in the template
    priceBeforeBoostOdds = computed(() =>
        this.oddsFormatter.format(this.resultState().picks[0].clientPick?.acceptedPrice?.price?.nativeOdds, {
            displayFormat: this.oddsDisplayFormat,
        }),
    );

    //this is only used in case of single pick which guranteed by isSinglePickPriceBoost in the template
    priceBoostOdds = computed(() => {
        const nativeOdds = this.resultState().picks[0].clientPick?.boostedPriceDetails?.boostedPrice?.nativeOdds;

        return this.oddsFormatter.format(nativeOdds, {
            displayFormat: this.oddsDisplayFormat,
        });
    });
    protected readonly NgxFloatUiPlacements = NgxFloatUiPlacements;
}
